import React, { type ComponentType } from 'react';
import type { ExecutionResult, MutationFunctionOptions } from 'react-apollo';
import type { MutationUpdaterFn } from 'apollo-client';

import type { AnnotationInfo } from '@atlaskit/editor-plugin-annotation/types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { doc } from '@atlaskit/adf-utils/doc';
import { mention } from '@atlaskit/adf-utils/mention';
import { paragraph as p } from '@atlaskit/adf-utils/paragraph';
import { text } from '@atlaskit/adf-utils/text';

import type { MutationFunctionInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import {
	REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	REPLY_TO_PAGE_COMMENT_PUBLISH_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import type { EditorCommentType } from '@confluence/comment/entry-points/commentTypes.ts';
import { isOptimisticSaveEnabledForTypeAndMode } from '@confluence/comment/entry-points/draftUtils.ts';
import { type AddCommentInCacheProps } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { getOptimisticResponseForInlineReply } from '@confluence/comments-data-manager/entry-points/useCreateInlineReplyMutation.ts';
import {
	addEmptyObjectRepliesAndReactions,
	getOptimisticResponseForObjectComment,
} from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutation.ts';
import {
	type useCreateObjectCommentMutation$data,
	type useCreateObjectCommentMutation$variables,
} from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutationTypes.ts';
import type { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import {
	handleInlineAnnotationNavigation,
	handleFooterCommentsNavigation,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';
import {
	handleMutationFailure,
	handleCreateReplySuccess as handleCreateInlineReplySuccess,
	updateApolloCacheCallback as updateInlineApolloCacheCallback,
	getCommentCreationLocation,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsUtils.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import type { CommentLocation } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import type { GraphQLContentStatus } from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery.ts';
import {
	updateApolloCacheReplyCallback as updateApolloCacheGeneralReplyCallback,
	handleCreateReplySuccess as handleCreateGeneralReplySuccess,
	type UpdateApolloCacheReplyCallbackDataType,
} from '@confluence/page-comments-queries/entry-points/pageCommentUtils.ts';
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { PassThroughProps } from '@confluence/comment/entry-points/CommentBatchList.ts';
import type { CommentData as NewCommentData } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import type {
	CommentData,
	ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { ViewReplyOptions } from '@confluence/comments-common/entry-points/repliesHelpers.ts';
import type { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import type {
	ReplyToAgentUser as CommentsPanelReplyToAgentUser,
	ReplyToCommentUser,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import {
	Platform,
	ContentRepresentation,
} from '@confluence/page-comments-queries/entry-points/__types__/CreateGeneralReplyMutation.ts';
import type { CreateGeneralReplyMutationVariables } from '@confluence/page-comments-queries/entry-points/__types__/CreateGeneralReplyMutation.ts';
import type { ContentTypesCommentService } from '@confluence/content-types-bridge-comments';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import type {
	CommentRepliesCreateGeneralReplyMutation as CommentRepliesCreateGeneralReplyMutation$data,
	CommentRepliesCreateGeneralReplyMutationVariables as CommentRepliesCreateGeneralReplyMutation$variables,
} from '../components/__types__/CommentRepliesCreateGeneralReplyMutation.ts';
import type {
	CommentRepliesCreateInlineReplyMutation as CommentRepliesCreateInlineReplyMutation$data,
	CommentRepliesCreateInlineReplyMutationVariables as CommentRepliesCreateInlineReplyMutation$variables,
} from '../components/__types__/CommentRepliesCreateInlineReplyMutation.ts';

export type ReplyToAgentUser = CommentsPanelReplyToAgentUser;

export const DEFAULT_NUM_OF_VISIBLE_REPLIES_FOR_RESOLVED_OR_DANGLING_COMMENT = 0;
export const DEFAULT_NUM_VISIBLE_REPLIES = 2;
export const STANDALONE_SHOW_ALL_REPLIES_CUTOFF = 5;

type HandleCommentThreadSelectionProps = {
	parentComment: CommentData;
	pageMode: PageMode;
	setCurrentlySelectedThreadKey: (
		parentCommentMarkerRef: string | undefined,
		commentType?: CommentType,
	) => void;
	event?: React.MouseEvent;
	handleSelectThread?: (threadId?: string) => void;
};

type HandlePanelSelectionAndScrollProps = {
	threadKey: string | null;
	isResolved: boolean;
	pageMode: PageMode;
	setCurrentlySelectedThreadKey: (
		parentCommentMarkerRef: string | undefined,
		commentType?: CommentType,
	) => void;
	commentType: CommentType;
	event?: React.MouseEvent;
	id?: string;
};

const shouldTriggerCommentScroll = (event: React.MouseEvent, id?: string): boolean => {
	if (!id) {
		return false;
	}
	const target = event.target as HTMLElement;
	const isContainer = target === event.currentTarget;
	const isCommentBody = Boolean(target.closest(`[data-testid="comment-thread-${id}-parent"]`));
	const isCommentReplyBody = Boolean(
		target.closest(`[data-testid="comment-thread-${id}-reply-list"]`),
	);
	const replyToggler = Boolean(target.closest('[data-testid="replies-toggler-container"]'));

	// Don't trigger if clicking on specific interactive elements
	const isInteractiveElement = Boolean(
		target.closest('button, a, input, textarea, [role="button"]'),
	);
	const isEmojiElement = Boolean(
		target.closest('[data-testid="emoji-add-icon"], [data-emoji-short-name]'),
	);

	const isGeneralReplyScroller = Boolean(
		target.closest('[data-testid="comments-panel-reply-scroller-container"]'),
	);
	const isFooterCommentThreadLoaded = Boolean(
		document.getElementById(`footer-comment-thread-${id}-container`),
	);

	// NOTE - handles scrolling in place of the general reply scroller when the footer comment thread is not yet in the DOM
	// This is a temporary solution until we update our data loading patterns. Once they're updated, this will always be false and can be removed.
	const scrollInPlaceOfGeneralReplyScroller =
		isGeneralReplyScroller &&
		!isFooterCommentThreadLoaded &&
		fg('confluence_frontend_content_wrapper');

	return (
		(isContainer ||
			isCommentBody ||
			isCommentReplyBody ||
			replyToggler ||
			isGeneralReplyScroller) &&
		(!isInteractiveElement || scrollInPlaceOfGeneralReplyScroller) &&
		!isEmojiElement
	);
};

/**
 * Determines how many replies should be visible by default before collapsing.
 *
 * Returns:
 * - For resolved/deleted comments (with feature flag): DEFAULT_NUM_OF_VISIBLE_REPLIES_FOR_RESOLVED_OR_DANGLING_COMMENT
 * - For standalone comments with few replies: Shows all replies up to STANDALONE_SHOW_ALL_REPLIES_CUTOFF
 * - For all other cases: DEFAULT_NUM_VISIBLE_REPLIES
 */
export const getDefaultNumVisibleReplies = (
	numberOfReplies: number,
	isStandaloneComment: boolean = false,
	isParentCommentClosedOrDangling: boolean = false,
) => {
	if (isParentCommentClosedOrDangling && fg('confluence_frontend_new_dangling_comments_ux')) {
		return DEFAULT_NUM_OF_VISIBLE_REPLIES_FOR_RESOLVED_OR_DANGLING_COMMENT;
	}

	if (isStandaloneComment && numberOfReplies <= STANDALONE_SHOW_ALL_REPLIES_CUTOFF) {
		return STANDALONE_SHOW_ALL_REPLIES_CUTOFF;
	}

	return DEFAULT_NUM_VISIBLE_REPLIES;
};

export const getExperienceName = (isInlineComment: boolean) =>
	isInlineComment
		? REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE
		: REPLY_TO_PAGE_COMMENT_PUBLISH_EXPERIENCE;

export const handleCommentThreadSelection = ({
	parentComment,
	pageMode,
	setCurrentlySelectedThreadKey,
	event,
	handleSelectThread,
}: HandleCommentThreadSelectionProps): void => {
	const threadKey =
		parentComment.type === CommentType.INLINE
			? (parentComment.location as CommentLocation).inlineMarkerRef
			: parentComment.id;
	const isResolved = !parentComment.isOpen;

	// Send a message via bridge api if applicable
	if (threadKey && handleSelectThread) {
		handleSelectThread(threadKey);
	}

	handlePanelSelectionAndScroll({
		threadKey: threadKey ?? null,
		isResolved,
		pageMode,
		setCurrentlySelectedThreadKey,
		commentType: parentComment.type,
		event,
		id: parentComment.id,
	});
};

export const handlePanelSelectionAndScroll = ({
	threadKey,
	isResolved,
	pageMode,
	setCurrentlySelectedThreadKey,
	commentType,
	event,
	id,
}: HandlePanelSelectionAndScrollProps): void => {
	// We only scroll the document for non-resolved inline comments or page comments
	if (threadKey && !isResolved) {
		if (event && !shouldTriggerCommentScroll(event, id)) {
			setCurrentlySelectedThreadKey(threadKey);
		} else {
			setCurrentlySelectedThreadKey(threadKey, commentType);
		}
		if (commentType === CommentType.INLINE) {
			const commentElement = document.getElementById(threadKey);
			if (commentElement) {
				// Determine navigation context based on page mode
				const navigationContext =
					pageMode === PageMode.EDIT || pageMode === PageMode.LIVE
						? NavigationContext.EDITOR
						: NavigationContext.RENDERER;

				handleInlineAnnotationNavigation({
					scrollBehavior: ScrollBehavior.SMOOTH,
					context: navigationContext,
					block: 'center',
					inline: 'nearest',
					markElement: commentElement,
					annotationId: threadKey,
				});
			}
		} else if (commentType === CommentType.GENERAL) {
			void handleFooterCommentsNavigation({
				scrollBehavior: ScrollBehavior.SMOOTH,
				block: 'center',
				inline: 'nearest',
				commentId: threadKey,
			});
		}
	}
};

type ReplyToCommentArgs = {
	parentCommentId?: string;
	user?: ReplyToCommentUser;
} & Partial<Record<'location', LocationValue>>;

export type SaveProps = {
	userId: string | null;
	displayName: string | null;
	pageId: string;
	parentCommentId: string;
	cloudId: string;
	adf: object;
	onSuccess: () => void;
	pageMode: PageMode;
	editCommentQueryId?: string;
	setCommentForEdit: React.Dispatch<React.SetStateAction<string>>;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	pageType: string;
	experienceTracker: ExperienceTrackerAPI;
	createInlineReplyFn?: (
		options:
			| MutationFunctionOptions<
					CommentRepliesCreateInlineReplyMutation$data,
					CommentRepliesCreateInlineReplyMutation$variables
			  >
			| undefined,
		onCacheUpdateError: (error: Error) => void,
	) => Promise<ExecutionResult<CommentRepliesCreateInlineReplyMutation$data>>;
	// TODO: Remove this prop when the new Comments Data Manager is fully adopted
	createGeneralReplyFn?: (
		options?:
			| MutationFunctionOptions<
					UpdateApolloCacheReplyCallbackDataType,
					CommentRepliesCreateGeneralReplyMutation$variables
			  >
			| undefined,
	) => Promise<ExecutionResult<CommentRepliesCreateGeneralReplyMutation$data>>;
	createObjectReplyFn?: MutationFunctionInterop<
		useCreateObjectCommentMutation$data,
		useCreateObjectCommentMutation$variables
	>;
	addCommentInCommentsDataManagerCacheFn?: (props: AddCommentInCacheProps) => void;
	createFooterReplyFn?: (
		options?:
			| MutationFunctionOptions<
					UpdateApolloCacheReplyCallbackDataType,
					CreateGeneralReplyMutationVariables
			  >
			| undefined,
	) => Promise<ExecutionResult<CommentRepliesCreateGeneralReplyMutation$data>>;
	selectedAnnotation?: AnnotationInfo;
	addReplyToCommentThread: (ref: string, reply: ReplyData, commentType: CommentType) => void;
	threadKey: string;
	commentType: CommentType;
	searchSessionId?: string | null;
	isReactionsEnabled?: boolean;
	analyticsSource: AnalyticsSource;
	contentTypesCommentService?: ContentTypesCommentService;
	isFooterComment?: boolean;
	setReplyToCommentId: (args_0?: ReplyToCommentArgs | undefined) => void;
	resetContentChanged: () => void;
	setReplyView: (view: ViewReplyOptions) => void;
};

const getEditorCommentType = (commentType: CommentType, pageMode: PageMode): EditorCommentType => {
	if (commentType === CommentType.INLINE) {
		return pageMode === PageMode.VIEW ? 'inline' : 'edit-inline';
	} else {
		// i.e. commentType === CommentType.GENERAL
		return 'page';
	}
};

// The 4 `build*Options` functions build mutation options for creating replies,
// split by reply kind to keep the main flow concise.

// Exported only for testing
export const buildInlineReplyOptions = ({
	pageId,
	parentCommentId,
	adf,
	isEditor,
	pageMode,
	threadKey,
	includeReactionsFlag,
	// Below are only used when both CSM and optimistic reply are enabled
	userId,
	displayName,
}: {
	pageId: string;
	parentCommentId: string;
	adf: object;
	isEditor: boolean;
	pageMode: PageMode;
	threadKey: string;
	includeReactionsFlag: boolean;
	userId?: string | null;
	displayName?: string | null;
}) => ({
	variables: {
		input: {
			containerId: pageId,
			parentCommentId,
			commentBody: {
				value: JSON.stringify(adf),
				representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
			},
			createdFrom: getCommentCreationLocation(isEditor, pageMode === PageMode.LIVE),
		},
		pageId,
		includeReactions: includeReactionsFlag,
	},
	update: updateInlineApolloCacheCallback(
		'create',
		{
			pageId,
			contentStatus: ['CURRENT', 'DRAFT'] as GraphQLContentStatus[],
			annotationId: threadKey,
		},
		parentCommentId,
	) as MutationUpdaterFn<CommentRepliesCreateInlineReplyMutation$data>,
	optimisticResponse: isOptimisticSaveEnabledForTypeAndMode(
		getEditorCommentType(CommentType.INLINE, pageMode),
		'reply',
	)
		? getOptimisticResponseForInlineReply({
				userId: userId ?? '',
				displayName: displayName ?? '',
				adf,
				pageId,
				parentCommentId,
				parentCommentAnnotationId: threadKey,
			})
		: undefined,
});

const buildGeneralReplyOptions = ({
	pageId,
	cloudId,
	parentCommentId,
	adf,
	includeReactionsFlag,
	isReactionsEnabled,
}: {
	pageId: string;
	cloudId: string;
	parentCommentId: string;
	adf: object;
	includeReactionsFlag: boolean;
	isReactionsEnabled?: boolean;
}) => ({
	variables: {
		pageId,
		cloudId,
		input: {
			commentBody: {
				value: JSON.stringify(adf),
				representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
			},
			commentSource: Platform.WEB,
			containerId: pageId,
			parentCommentId,
		},
		includeReactions: includeReactionsFlag,
	},
	update: updateApolloCacheGeneralReplyCallback(
		'create',
		{
			pageId,
			contentStatus: ['CURRENT', 'DRAFT'] as GraphQLContentStatus[],
			type: ['FOOTER'],
		},
		parentCommentId,
		isReactionsEnabled,
	) as MutationUpdaterFn<UpdateApolloCacheReplyCallbackDataType>,
});

// Exported only for testing
export const buildObjectReplyOptions = ({
	pageId,
	cloudId,
	parentCommentId,
	adf,
	addCommentInCommentsDataManagerCacheFn,
	experienceTracker,
	isInlineComment,
	userId,
	displayName,
}: {
	pageId: string;
	cloudId: string;
	parentCommentId: string;
	adf: object;
	addCommentInCommentsDataManagerCacheFn?: (props: AddCommentInCacheProps) => void;
	experienceTracker: ExperienceTrackerAPI;
	isInlineComment: boolean;
	userId: string | null;
	displayName: string | null;
}) => ({
	variables: {
		cloudId,
		input: {
			commentBody: {
				value: JSON.stringify(adf),
				representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
			},
			commentSource: Platform.WEB,
			containerId: pageId,
			parentCommentId,
		},
		pageId,
		includeReactionsSummary: !expValEquals(
			'confluence_fe_use_create_comment_exclude_reactions',
			'isEnabled',
			true,
		),
	},
	update: (...params: Parameters<MutationUpdaterFn<useCreateObjectCommentMutation$data>>): void => {
		const [_dataProxy, mutationResult] = params;
		addCommentInCommentsDataManagerCacheFn?.({
			newComment: addEmptyObjectRepliesAndReactions(mutationResult.data!.createFooterComment),
			onError: (error: Error) => {
				void handleMutationFailure({
					experienceTracker,
					experienceName: getExperienceName(isInlineComment),
					error,
					commentId: parentCommentId,
					shouldReturnError: true,
				});
			},
		});
	},
	optimisticResponse: isOptimisticSaveEnabledForTypeAndMode('page', 'reply')
		? getOptimisticResponseForObjectComment({
				userId: userId ?? '',
				displayName: displayName ?? '',
				adf,
				pageId,
				parentCommentId,
			})
		: undefined,
});

const buildFooterReplyOptions = ({
	pageId,
	cloudId,
	parentCommentId,
	adf,
	isReactionsEnabled,
}: {
	pageId: string;
	cloudId: string;
	parentCommentId: string;
	adf: object;
	isReactionsEnabled?: boolean;
}) => ({
	variables: {
		pageId,
		cloudId,
		input: {
			commentBody: {
				value: JSON.stringify(adf),
				representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
			},
			commentSource: Platform.WEB,
			containerId: pageId,
			parentCommentId,
		},
	},
	update: updateApolloCacheGeneralReplyCallback(
		'create',
		{
			pageId,
			contentStatus: ['CURRENT', 'DRAFT'] as GraphQLContentStatus[],
			type: ['FOOTER'],
		},
		parentCommentId,
		isReactionsEnabled,
	) as MutationUpdaterFn<UpdateApolloCacheReplyCallbackDataType>,
});

export const onSaveReply = async ({
	pageId,
	parentCommentId,
	cloudId,
	adf,
	onSuccess,
	pageMode,
	editCommentQueryId,
	setCommentForEdit,
	createAnalyticsEvent,
	pageType,
	experienceTracker,
	createInlineReplyFn,
	createGeneralReplyFn,
	createObjectReplyFn, // Create reply via Comments Data Manager
	addCommentInCommentsDataManagerCacheFn, // Add comment to Comments Data Manager cache, passed in because it comes from a hook
	createFooterReplyFn,
	selectedAnnotation,
	addReplyToCommentThread,
	threadKey,
	commentType,
	searchSessionId,
	isReactionsEnabled,
	analyticsSource,
	contentTypesCommentService,
	isFooterComment,
	setReplyToCommentId,
	resetContentChanged,
	setReplyView,
	userId,
	displayName,
}: SaveProps) => {
	const isInlineComment = commentType === CommentType.INLINE;
	const isEditor = pageMode === PageMode.EDIT;

	const startExperienceName = getExperienceName(isInlineComment);
	experienceTracker.start({
		name: startExperienceName,
		timeout: ExperienceTimeout.EDITOR_PUBLISH,
		attributes: getCommentsDataManagerExperimentAttributes(),
	});

	let createReplyPromise = undefined;
	if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
		// New path - improved logic + Comments Data Manager support
		if (isInlineComment) {
			createReplyPromise = createInlineReplyFn?.(
				buildInlineReplyOptions({
					pageId,
					parentCommentId,
					adf,
					isEditor,
					pageMode,
					threadKey,
					includeReactionsFlag: false,
					userId,
					displayName,
				}),
				(error: Error) => {
					void handleMutationFailure({
						experienceTracker,
						experienceName: getExperienceName(isInlineComment),
						error,
						commentId: parentCommentId,
						shouldReturnError: true,
					});
				},
			);
		} else if (createObjectReplyFn) {
			// The createObjectReplyFn takes precedence over other non-inline (footer or general) reply
			// functions, if it was provided
			createReplyPromise = createObjectReplyFn(
				buildObjectReplyOptions({
					pageId,
					cloudId,
					parentCommentId,
					adf,
					addCommentInCommentsDataManagerCacheFn,
					experienceTracker,
					isInlineComment,
					userId,
					displayName,
				}),
			);
		} else if (isFooterComment) {
			createReplyPromise = createFooterReplyFn?.(
				buildFooterReplyOptions({
					pageId,
					cloudId,
					parentCommentId,
					adf,
					isReactionsEnabled,
				}),
			);
		} else {
			createReplyPromise = createGeneralReplyFn?.(
				buildGeneralReplyOptions({
					pageId,
					cloudId,
					parentCommentId,
					adf,
					includeReactionsFlag: false,
					isReactionsEnabled,
				}),
			);
		}
	} else {
		// Legacy path - use old logic
		createReplyPromise = isInlineComment
			? createInlineReplyFn &&
				createInlineReplyFn(
					buildInlineReplyOptions({
						pageId,
						parentCommentId,
						adf,
						isEditor,
						pageMode,
						threadKey,
						includeReactionsFlag: false,
					}),
					() => {},
				)
			: isFooterComment
				? createFooterReplyFn &&
					createFooterReplyFn(
						buildFooterReplyOptions({
							pageId,
							cloudId,
							parentCommentId,
							adf,
							isReactionsEnabled,
						}),
					)
				: createGeneralReplyFn &&
					createGeneralReplyFn(
						buildGeneralReplyOptions({
							pageId,
							cloudId,
							parentCommentId,
							adf,
							includeReactionsFlag: false,
							isReactionsEnabled,
						}),
					);
	}

	const isOptimisticReplyEnabled = isOptimisticSaveEnabledForTypeAndMode(
		getEditorCommentType(commentType, pageMode),
		'reply',
	);
	if (isOptimisticReplyEnabled) {
		// Immediately reset reply state before the createReplyPromise resolves
		setReplyToCommentId({ parentCommentId: undefined });
		setReplyView(ViewReplyOptions.ALL);
	}

	return (
		createReplyPromise &&
		createReplyPromise
			.then(({ data }: any) => {
				const replyResult = isInlineComment ? data.replyInlineComment : data.createFooterComment;
				const reply: ReplyData = {
					type: isInlineComment ? CommentType.INLINE : CommentType.GENERAL,
					...replyResult,
				};
				if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					addReplyToCommentThread(threadKey, reply, commentType);
				}

				// Update content types comments if we are creating a reply on a different content type
				contentTypesCommentService?.onUpdateComment?.(threadKey, 'reply');

				if (!isOptimisticReplyEnabled) {
					// Reset reply state after successful creation - already done if optimistic reply is enabled
					setReplyToCommentId({ parentCommentId: undefined });
					setReplyView(ViewReplyOptions.ALL);
				}
				resetContentChanged();

				if (isInlineComment) {
					handleCreateInlineReplySuccess({
						onSuccess,
						editCommentQueryId,
						setCommentForEdit,
						data,
						createAnalyticsEvent,
						pageId,
						pageType,
						analyticsSource,
						commentId: parentCommentId,
						pageMode,
						selectedAnnotation,
						getInlineNodeTypes: undefined,
						experienceTracker,
						isEditor,
					});
				} else {
					handleCreateGeneralReplySuccess({
						onSuccess,
						editCommentQueryId,
						setCommentForEdit,
						data,
						createAnalyticsEvent,
						pageId,
						pageType,
						analyticsSource,
						commentId: parentCommentId,
						pageMode,
						experienceTracker,
						searchSessionId,
						isFooterComment,
					});
				}
			})
			.catch((error: any) => {
				let failExperienceName;
				if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					failExperienceName = getExperienceName(isInlineComment);
				} else {
					failExperienceName = REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE;

					if (!isInlineComment) {
						failExperienceName = REPLY_TO_PAGE_COMMENT_PUBLISH_EXPERIENCE;
					}
				}

				return handleMutationFailure({
					experienceTracker,
					experienceName: failExperienceName,
					error,
					commentId: parentCommentId,
					shouldReturnError: true,
				});
			})
	);
};

export const getCommentThreadsToBatch: (
	comments: (CommentData | NewCommentData)[],
	passThroughProps: PassThroughProps,
	CommentThreadComponent: ComponentType<any>,
	transitionIndexWithinBatch: number | undefined,
) => React.JSX.Element[] = (
	comments,
	passThroughProps,
	CommentThreadComponent,
	transitionIndexWithinBatch,
) =>
	comments.map((parentComment, index) => {
		const shouldRenderCommentTypeSeparator = index === transitionIndexWithinBatch;

		return (
			<CommentThreadComponent
				// Note: Cannot let `key` use `parentComment.id`, which, when optimistic save
				// (COMMENTS-6792) is enabled, is the fake optimistic id first and then gets updated
				// to the actual new comment id after mutation succeeds.
				// When mutation succeeds, DOM tree is updated. If `key` stays the same, the update
				// is recognized as multiple individual "mutation:attribute" for known attributes
				// excluded from VC calculation (e.g. data-testid); if key changes, the update is
				// recognized as a replacement of the entire element tree and counted in VC.
				// Unlike `parentComment.id`, `index` is stable as long as the comment thread's
				// position on the page does not change.
				key={`comment-thread-${index}`}
				parentComment={parentComment}
				shouldRenderCommentTypeSeparator={shouldRenderCommentTypeSeparator}
				{...passThroughProps}
			/>
		);
	});

export const hasUnreadHiddenReply = (reply: ReplyData, hiddenReplies: ReplyData[]): boolean => {
	return hiddenReplies.some((hiddenReply: ReplyData) => hiddenReply.id === reply.id);
};

export const getDefaultReplyView = (
	numberOfReplies: number,
	isParentCommentResolvedOrDangling: boolean,
	isStandaloneComment: boolean = false,
) => {
	if (isParentCommentResolvedOrDangling) {
		return ViewReplyOptions.HIDDEN;
	}

	if (
		isStandaloneComment ||
		numberOfReplies <= getDefaultNumVisibleReplies(numberOfReplies, isStandaloneComment)
	) {
		return ViewReplyOptions.ALL;
	}

	return ViewReplyOptions.LIMITED;
};

export const getAdfForQuickReplyMention = (replyToCommentUser: ReplyToCommentUser | undefined) => {
	if (!replyToCommentUser || !replyToCommentUser.displayName || !replyToCommentUser.accountId) {
		return undefined;
	}

	return doc(
		p(
			mention({
				id: replyToCommentUser.accountId,
				text: `@${replyToCommentUser.displayName}`,
			}),
			text(' '),
		),
	);
};

export const getAdfForAgentReplyMention = (
	commentUser: ReplyToCommentUser | undefined,
	agentUser: ReplyToAgentUser | undefined,
) => {
	if (!agentUser || !agentUser.displayName || !agentUser.accountId) {
		return undefined;
	}

	const mentionNodes = [
		mention({
			id: agentUser.accountId,
			text: `@${agentUser.displayName}`,
		}),
		text(' '),
	];

	if (commentUser?.accountId && commentUser?.displayName) {
		mentionNodes.push(
			mention({
				id: commentUser.accountId,
				text: `@${commentUser.displayName}`,
			}),
			text(' '),
		);
	}

	return doc(p(...mentionNodes));
};

export const getReplyEditorContent = ({
	replyToCommentUser,
	replyToAgentUser,
}: {
	replyToCommentUser: ReplyToCommentUser | undefined;
	replyToAgentUser: ReplyToAgentUser | undefined;
}) => {
	if (!fg('confluence_frontend_content_wrapper')) {
		return undefined;
	}

	if (expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)) {
		return (
			getAdfForAgentReplyMention(replyToCommentUser, replyToAgentUser) ??
			getAdfForQuickReplyMention(replyToCommentUser)
		);
	}

	return getAdfForQuickReplyMention(replyToCommentUser);
};
